import {
  Box,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ContentSkip } from 'shared-components';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'student/src/store';
import { getNavigationUrls } from 'student/src/store/config/actions';
import { ImportSource } from 'types';
import MobileNavbar from './MobileNavbar';
import SidebarContents from './SidebarContents';
import { ISidebarContentsProps, ISidebarProps } from './types';

const Sidebar = (props: ISidebarProps) => {
  const {
    student_name,
    onLogout,
    student_number,
    sections,
    activeSectionId,
    completeLabel,
    completeDesc,
    onSectionSelect,
    pageButtons
  } = props;
  const dispatch = useDispatch();

  // Get data from Redux store
  const navigationUrls = useSelector((state: ApplicationState) => state.config.navigationUrls);
  const importData = useSelector((state: ApplicationState) => state.student.importData);
  const university = useSelector((state: ApplicationState) => state.student.core?.university);

  // Check if student has Apply access
  const hasApplyAccess = importData?.source === ImportSource.APPLY || !!importData?.applicantId;

  useEffect(() => {
    if (university && hasApplyAccess) {
      dispatch(getNavigationUrls(university));
    }
  }, [university, hasApplyAccess, dispatch]);

  const { isOpen, onClose, onToggle } = useDisclosure();

  const sidebarContentsProps: ISidebarContentsProps = {
    student_name,
    onLogout,
    student_number,
    sections,
    activeSectionId,
    completeLabel,
    completeDesc,
    onSectionSelect,
    onClose,
    navigationUrls,
    hasApplyAccess,
    importData
  };

  const MobileSidebar = () => {
    const btnRef = React.useRef<HTMLButtonElement>(null);

    return (
      <>
        <MobileNavbar>
          <Tooltip hasArrow label="Open Task list">
            <IconButton
              aria-label="Task list"
              onClick={onToggle}
              icon={<FontAwesomeIcon icon={faBars} size="lg" />}
              size="lg"
              variant="ghost"
              colorScheme="gray"
              borderRadius="full"
            />
          </Tooltip>

          {pageButtons}
        </MobileNavbar>

        <Drawer placement="left" isOpen={isOpen} onClose={onClose} finalFocusRef={btnRef}>
          <DrawerOverlay />

          <DrawerContent maxWidth="20rem">
            <DrawerCloseButton color="whiteAlpha.600" _hover={{ bgColor: 'whiteAlpha.100' }} />

            <SidebarContents {...sidebarContentsProps} />
          </DrawerContent>
        </Drawer>
      </>
    );
  };

  const isLargeScreen = useBreakpointValue({ base: false, lg: true });

  return (
    <>
      <ContentSkip />
      {isLargeScreen ? (
        <Box width={{ lg: '22rem', xl: '24rem' }} position="fixed" top={0} bottom={0} left={0}>
          <SidebarContents {...sidebarContentsProps} />
        </Box>
      ) : (
        <MobileSidebar />
      )}
    </>
  );
};

export default Sidebar;
